import { apiClient } from '../apiClient';
import { Device, DeviceGroup, ReadoutPlan, Appliance, Alert } from '../../types';

/**
 * Fetches all devices
 */
export const fetchDevices = async (): Promise<Device[]> => {
  const response = await apiClient.get('/api/devices');
  return response.data;
};

/**
 * Fetches a single device by ID
 */
export const fetchDeviceById = async (id: string): Promise<Device> => {
  const response = await apiClient.get(`/api/devices/${id}`);
  return response.data;
};

/**
 * Fetches all device groups
 */
export const fetchDeviceGroups = async (): Promise<DeviceGroup[]> => {
  const response = await apiClient.get('/api/devices/groups');
  return response.data;
};

/**
 * Fetches readout plans for a device
 */
export const fetchReadoutPlans = async (deviceId: string): Promise<ReadoutPlan[]> => {
  const response = await apiClient.get(`/api/devices/${deviceId}/readoutplans`);
  return response.data.map((plan: any) => ({
    ...plan,
    createdAt: new Date(plan.createdAt),
    time: new Date(plan.time),
  }));
};

/**
 * Fetches appliances for a device
 */
export const fetchAppliances = async (deviceId: string): Promise<Appliance[]> => {
  const response = await apiClient.get(`/api/devices/${deviceId}/appliances`);
  return response.data.map((appliance: Appliance) => ({
    ...appliance,
    timestamp: new Date(appliance.timestamp),
  }));
};

/**
 * Creates a new device
 */
export const createDevice = async (device: Device): Promise<Device> => {
  const response = await apiClient.post('/api/devices', device);
  return response.data;
};

/**
 * Updates an existing device
 */
export const updateDevice = async (device: Device): Promise<Device> => {
  if (!device.id) {
    throw new Error('Device ID is required for update');
  }
  const response = await apiClient.put(`/api/devices/${device.id}`, device);
  return response.data;
};

/**
 * Saves a device (creates or updates)
 */
export const saveDevice = async (device: Device): Promise<Device> => {
  if (device.id) {
    return updateDevice(device);
  } else {
    return createDevice(device);
  }
};

/**
 * Resolves pending device invitations
 */
export const resolvePendingInvitations = async (): Promise<void> => {
  await apiClient.post('/api/devices/pending-registration');
};

/**
 * Fetches alerts for a device
 * @param deviceId - The device ID
 * @param from - Optional date to filter alerts from
 */
export const fetchDeviceAlerts = async (deviceId: string, from?: Date): Promise<Alert[]> => {
  const params = from ? { from } : {};
  const response = await apiClient.get(`/api/devices/${deviceId}/alerts`, { params });
  return response.data;
};
